import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Editor from './Editor';
import Step3_FinalCard from './Step3_FinalCard';
import CardViewPage from './pages/CardViewPage';
import DashboardPage from './pages/DashboardPage';
import NebsPage from './pages/NebsPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { keyMap, compressData, decompressData, reverseKeyMap, reverseValueMap } from './utils/cardState';
import './App.css';

// 로그인 + 저장된 카드 있을 때 상단 배너
function MyCardBanner() {
  const { user, cardStatus, savedCard, unreadCount } = useAuth();
  if (!user) return null;
  return (
    <div style={{
      position: 'fixed', top: 12, right: 16, zIndex: 1000,
      background: '#fff', border: '1px solid #e5e5e5', borderRadius: '8px',
      padding: '6px 14px', fontSize: '0.8em', color: '#333',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      display: 'flex', gap: 12, alignItems: 'center',
    }}>
      {cardStatus === 'verified' && savedCard?.shortId && (
        <a href={`/card/${savedCard.shortId}`} style={{ color: '#333', textDecoration: 'none' }}>
          내 명함 →
        </a>
      )}
      <a href="/nebs" style={{ color: '#333', textDecoration: 'none', fontSize: '0.8em' }}>
        ✦ Neb
      </a>
      <a href="/messages" style={{
        color: '#333', textDecoration: 'none',
        display: 'flex', alignItems: 'center', gap: 5, position: 'relative',
      }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
          <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.708 2.825L15 11.105V5.383zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741zM1 11.105l4.708-2.897L1 5.383v5.722z"/>
        </svg>
        쪽지함
        {unreadCount > 0 && (
          <span style={{
            background: '#000', color: '#fff', borderRadius: '10px',
            fontSize: '0.75em', padding: '1px 6px', lineHeight: '1.4',
          }}>
            {unreadCount}
          </span>
        )}
      </a>
    </div>
  );
}

const valueMap = {
  'h-layout1': 'h1', 'h-layout2': 'h2', 'h-layout3': 'h3', 'v-layout1': 'v1',
  'Pretendard': 'p', 'Noto Sans KR': 'n', 'Montserrat': 'm', 'Playfair Display': 'd',
  'name': 'n', 'company': 'c',
  'center': 'c', 'top': 't',
};

function AppEditor() {
  const [step, setStep] = useState(1);
  // ... (state initializations are the same)
  const [cardData, setCardData] = useState({
    details: { name: '홍길동', title: '소프트웨어 엔지니어', company: 'Gemini Inc.', email: 'gildong.hong@gemini.com', phone: '010-1234-5678', website: 'gemini.google.com', sns: 'instagram.com/google', address: '1600 Amphitheatre Parkway, Mountain View, CA', logo: '', videoUrl: '', engName: '', tagline: '', videoOverlay: '' },
    displayOptions: { title: true, company: true, email: true, phone: true, website: true, sns: false, address: false }
  });
  const [layout, setLayout] = useState('h-layout1');
  const [orientation, setOrientation] = useState('horizontal');
  const [font, setFont] = useState('Pretendard');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [emphasis, setEmphasis] = useState('name');
  const [verticalAlign, setVerticalAlign] = useState('center');
  const [showLogo, setShowLogo] = useState(true);
  const [qrOptions, setQrOptions] = useState({ dotsOptions: { type: 'rounded', color: '#000000' }, backgroundOptions: { color: '#ffffff' } });
  const [isComplete, setIsComplete] = useState(false);
  const [shortId, setShortId] = useState(() => localStorage.getItem('myCardShortId') || '');
  const isInitialMount = useRef(true);

  // Effect for Initial Load from URL
  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    const compressedParam = params.get('d');

    if (compressedParam) {
      const decompressedString = decompressData(decodeURIComponent(compressedParam));
      if (decompressedString) {
        params = new URLSearchParams(decompressedString);
      }
    }
    
    const urlData = { details: {}, displayOptions: {} };
    
    params.forEach((value, key) => {
      const fullKey = reverseKeyMap[key];
      if (!fullKey) return;

      if (fullKey in cardData.details) {
        urlData.details[fullKey] = value;
      } else if (fullKey.startsWith('show_')) {
        const optionKey = fullKey.replace('show_', '');
        if (optionKey in cardData.displayOptions) {
          urlData.displayOptions[optionKey] = value === 'true';
        }
      }
    });

    if (Object.keys(urlData.details).length > 0 || Object.keys(urlData.displayOptions).length > 0) {
      setCardData(prevData => ({
        details: { ...prevData.details, ...urlData.details },
        displayOptions: { ...prevData.displayOptions, ...urlData.displayOptions },
      }));
    }

    const setters = {
      ly: (v) => setLayout(reverseValueMap.ly[v] || 'h-layout1'),
      ft: (v) => setFont(reverseValueMap.ft[v] || 'Pretendard'),
      ep: (v) => setEmphasis(reverseValueMap.ep[v] || 'name'),
      va: (v) => setVerticalAlign(reverseValueMap.va[v] || 'center'),
      bg: setBackgroundColor,
      sl: (v) => setShowLogo(v === 'true'),
      qdt: (v) => setQrOptions(o => ({ ...o, dotsOptions: { ...o.dotsOptions, type: v } })),
      qdc: (v) => setQrOptions(o => ({ ...o, dotsOptions: { ...o.dotsOptions, color: v } })),
      qbc: (v) => setQrOptions(o => ({ ...o, backgroundOptions: { ...o.backgroundOptions, color: v } })),
      x: (v) => setIsComplete(v === 'true'),
      sid: (v) => setShortId(v),
    };

    params.forEach((value, key) => {
      if (setters[key]) setters[key](value);
    });
    
    if (params.get(keyMap.layout) && params.get(keyMap.layout).startsWith('v')) {
      setOrientation('vertical');
    }

  }, []);

  // Effect for Updating URL as state changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    // Set details
    for (const key in cardData.details) {
      if (cardData.details[key]) params.set(keyMap[key], cardData.details[key]);
    }
    // Set display options
    for (const key in cardData.displayOptions) {
      params.set(keyMap[`show_${key}`], cardData.displayOptions[key]);
    }
    // Set other states
    params.set(keyMap.step, step);
    params.set(keyMap.layout, valueMap[layout] || layout);
    params.set(keyMap.font, valueMap[font] || font);
    params.set(keyMap.emphasis, valueMap[emphasis] || emphasis);
    params.set(keyMap.verticalAlign, valueMap[verticalAlign] || verticalAlign);
    params.set(keyMap.bgColor, backgroundColor);
    params.set(keyMap.showLogo, showLogo);
    params.set(keyMap.qrDotsType, qrOptions.dotsOptions.type);
    params.set(keyMap.qrDotsColor, qrOptions.dotsOptions.color);
    params.set(keyMap.qrBgColor, qrOptions.backgroundOptions.color);

    if (step === 2) {
      params.set(keyMap.complete, 'true');
    }

    const compressedData = compressData(params.toString());
    if (compressedData) {
      const newUrl = `${window.location.pathname}?d=${encodeURIComponent(compressedData)}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    } else {
      // Fallback to uncompressed URL if compression fails
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }

  }, [cardData, step, layout, font, backgroundColor, emphasis, verticalAlign, showLogo, qrOptions]);

  const nextStep = () => setStep(step + 1);
  const prevStep = () => {
    setIsComplete(false);
    setStep(step - 1);
  };

  const handleDataChange = (newCardData) => setCardData(newCardData);
  const handleLayoutChange = (selectedLayout) => setLayout(selectedLayout);
  const handleOrientationChange = (selectedOrientation) => {
    setOrientation(selectedOrientation);
    setLayout(selectedOrientation === 'horizontal' ? 'h-layout1' : 'v-layout1');
  };
  const handleFontChange = (selectedFont) => setFont(selectedFont);
  const handleBackgroundColorChange = (color) => setBackgroundColor(color);
  const handleEmphasisChange = (newEmphasis) => setEmphasis(newEmphasis);
  const handleVerticalAlignChange = (vAlign) => setVerticalAlign(vAlign);
  const handleShowLogoChange = (v) => setShowLogo(v);
  const handleQrOptionsChange = (newQrOptions) => setQrOptions(newQrOptions);

  const allProps = {
    cardData, layout, orientation, font, backgroundColor, emphasis, verticalAlign, showLogo, qrOptions, isComplete, shortId,
    onDataChange: handleDataChange,
    onLayoutChange: handleLayoutChange,
    onOrientationChange: handleOrientationChange,
    onFontChange: handleFontChange,
    onBackgroundColorChange: handleBackgroundColorChange,
    onEmphasisChange: handleEmphasisChange,
    onVerticalAlignChange: handleVerticalAlignChange,
    onShowLogoChange: handleShowLogoChange,
    onQrOptionsChange: handleQrOptionsChange,
    onNext: nextStep,
    onPrev: prevStep,
    onShortIdChange: setShortId,
  };

  if (step === 2) {
    return (
      <AuthProvider>
        <MyCardBanner />
        <Step3_FinalCard {...allProps} />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <MyCardBanner />
      <Container className="my-5">
        <Row className="justify-content-center">
          <Col md={10}>
            <h1 className="text-center mb-4">명함 에디터</h1>
            <Editor {...allProps} />
          </Col>
        </Row>
      </Container>
    </AuthProvider>
  );
}

function App() {
  const path = window.location.pathname;

  const cardPathMatch = path.match(/^\/card\/([a-z0-9]+)$/);
  if (cardPathMatch) {
    return (
      <AuthProvider>
        <CardViewPage shortId={cardPathMatch[1]} />
      </AuthProvider>
    );
  }

  if (path === '/dashboard' || path === '/messages') {
    return (
      <AuthProvider>
        <DashboardPage defaultTab={path === '/messages' ? 'messages' : 'messages'} />
      </AuthProvider>
    );
  }

  if (path === '/nebs') {
    return (
      <AuthProvider>
        <NebsPage />
      </AuthProvider>
    );
  }

  return <AppEditor />;
}

export default App;
